<template>
  <b-modal :id="modalId" :title="isEditing ? 'Edit Task' : 'Add New Task'" size="lg" centered @hidden="resetModal">
    <b-form @submit.prevent="handleSubmit">
      <!-- Title -->
      <b-form-group label="Title" label-for="task-title" label-class="form-label">
        <b-form-input id="task-title" v-model="formData.title" placeholder="Enter task title" required />
        <small v-if="isTitleInvalid" class="text-danger mt-1">This field is required.</small>
      </b-form-group>

      <!-- Description -->
      <b-form-group label="Description" label-for="task-description" label-class="form-label">
        <b-form-textarea
          id="task-description"
          v-model="formData.description"
          placeholder="Enter task description"
          rows="3"
          max-rows="6"
        />
      </b-form-group>

      <!-- URL -->
      <b-form-group :state="formData.url ? !urlError : null">
        <label for="task-url" class="form-label"
          >Resource URL<i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top
            title="Include Reference Materials — Provide relevant URLs for video tutorials, official documentation or other helpful learning resources."
            >info</i
          ></label
        >
        <b-form-input
          id="task-url"
          v-model="formData.url"
          type="url"
          placeholder="https://example.com/resource"
          :state="formData.url ? !urlError : null"
          @input="validateUrl"
        />
        <template v-if="formData.url && urlError" #invalid-feedback>
          {{ urlError }}
        </template>
      </b-form-group>

      <!-- Completion Mode -->
      <b-form-group label="Completion Mode" label-for="task-completion-mode" label-class="form-label">
        <b-form-radio-group
          id="task-completion-mode"
          v-model="formData.completion_mode"
          :options="completionModes"
          name="completion-mode"
          required
        ></b-form-radio-group>
      </b-form-group>
    </b-form>

    <template #modal-footer>
      <div class="w-100">
        <b-button variant="primary" class="float-right mr-2" @click="handleSubmit" :disabled="isSubmitting">
          <b-spinner small v-if="isSubmitting"></b-spinner>
          {{ isEditing ? 'Save' : 'Add' }} Task
        </b-button>
        <b-button variant="outline-secondary" class="float-right mr-2" @click="$bvModal.hide(modalId)">
          Cancel
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';

export default {
  name: 'TaskModal',
  props: {
    modalId: {
      type: String,
      required: true,
      default: 'task-modal',
    },
    task: {
      type: Object,
      default: () => ({
        title: '',
        description: '',
        url: '',
        completion_mode: 'checkbox',
      }),
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formData: {
        title: '',
        description: '',
        url: '',
        completion_mode: 'checkbox',
      },
      completionModes: [
        { text: 'Checkbox', value: 'checkbox' },
        { text: 'File Upload', value: 'upload' },
      ],
      isSubmitting: false,
      isTitleInvalid: false,
      urlError: '',
    };
  },
  validations() {
    return {
      formData: {
        title: { required },
        completion_mode: { required },
      },
    };
  },
  watch: {
    task: {
      handler(newVal) {
        this.formData = {
          title: '',
          description: '',
          url: '',
          ...newVal,
        };
        // Ensure completion_mode is always set, default to 'checkbox' if not provided
        if (!this.formData.completion_mode) {
          this.formData.completion_mode = 'checkbox';
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === this.modalId) {
        // Reset form data when modal is shown
        if (this.task) {
          this.formData = {
            ...this.formData, // Keep default values
            ...this.task, // Override with task props
            completion_mode: this.task.completion_mode || 'checkbox', // Ensure completion_mode is set
          };
        } else {
          // For new tasks, ensure completion_mode is set to 'checkbox'
          this.formData.completion_mode = 'checkbox';
        }
      }
    });
  },
  beforeDestroy() {
    this.$root.$off('bv::modal::show');
  },
  methods: {
    resetModal() {
      this.formData = {
        title: '',
        description: '',
        url: '',
        completion_mode: 'checkbox',
      };
      this.isTitleInvalid = false;
      this.$emit('reset');
    },
    validateUrl() {
      if (!this.formData.url) {
        this.urlError = '';
        return true;
      }
      try {
        // Basic URL validation
        new URL(this.formData.url);
        this.urlError = '';
        return true;
      } catch (e) {
        this.urlError = 'Please enter a valid URL (e.g., https://example.com)';
        return false;
      }
    },
    handleSubmit() {
      if (this.formData.title === '') {
        this.isTitleInvalid = true;
        return;
      }

      // Validate URL if it's provided
      if (this.formData.url && !this.validateUrl()) {
        return;
      }

      if (!this.$v.formData.$invalid) {
        this.isTitleInvalid = false;
        this.isSubmitting = true;
        this.$emit('submit', { ...this.formData });
        this.$bvModal.hide(this.modalId);
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Add any custom styles here if needed */
</style>
